import type { OnCourseTracerData } from "../types/on-course-tracer.types";

type Listener = () => void;

/**
 * On-Course Tracer Store
 *
 * Holds the per-shot screen-space tracer data emitted by the radar
 * via `/output/site_track`. Stored outside React state to avoid
 * re-renders; consumers read by shot id via `useSyncExternalStore`.
 */
export class OnCourseTracerStore {
  private store = new Map<string, OnCourseTracerData>();
  private listeners = new Map<string, Set<Listener>>();

  set(shotId: string, data: OnCourseTracerData): void {
    this.store.set(shotId, data);
    this.notify(shotId);
  }

  get(shotId: string): OnCourseTracerData | undefined {
    return this.store.get(shotId);
  }

  has(shotId: string): boolean {
    return this.store.has(shotId);
  }

  delete(shotId: string): void {
    this.store.delete(shotId);
    this.notify(shotId);
  }

  clear(): void {
    const shotIds = [...this.store.keys()];
    this.store.clear();
    shotIds.forEach((id) => this.notify(id));
  }

  subscribe(shotId: string, listener: Listener): () => void {
    if (!this.listeners.has(shotId)) {
      this.listeners.set(shotId, new Set());
    }
    this.listeners.get(shotId)!.add(listener);

    return () => {
      const set = this.listeners.get(shotId);
      if (!set) {
        return;
      }
      set.delete(listener);
      if (set.size === 0) {
        this.listeners.delete(shotId);
      }
    };
  }

  private notify(shotId: string): void {
    this.listeners.get(shotId)?.forEach((listener) => listener());
  }
}

export const onCourseTracerStore = new OnCourseTracerStore();
